import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  bigAmountFormat,
  getAuthUserCurrency,
  getCampaignTypeLabel,
  getMediaSourceLabel,
  buildCampaignDetailNavigation,
  composeEventMetricLabel,
  EVENT_METRIC_OPTION,
} from "../../utils";
import { useTableMaxHeight } from "../../hooks/useTableMaxHeight";
import { useScrollEndDetection } from "../../hooks/useScrollEndDetection";
import ListPaginationBar from "../common/ListPaginationBar";

export default function CampaignPerformanceTable({
  rows = [],
  loading,
  appRouteId,
  dateFrom,
  dateTo,
  eventMetricLabel,
  metrics,
}) {
  // One column per configured event metric; falls back to a single legacy column built from
  // eventMetricLabel when the caller hasn't passed the full `metrics` config list.
  const metricColumns = Array.isArray(metrics) && metrics.length
    ? metrics
    : [{ id: null, label: eventMetricLabel || EVENT_METRIC_OPTION.label }];

  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const tableScrollRef = useRef(null);
  const tableInnerRef = useRef(null);
  const tableMaxHeight = useTableMaxHeight(tableScrollRef);
  const isAtScrollEnd = useScrollEndDetection(tableScrollRef, tableInnerRef, rows.length);

  const total = rows.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, totalPages);

  const pageRows = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return rows.slice(start, start + pageSize);
  }, [rows, safePage, pageSize]);

  useEffect(() => {
    setPage(1);
  }, [rows]);

  const openCampaignDetail = (rowId) => {
    if (!appRouteId) return;
    const { path, state } = buildCampaignDetailNavigation(appRouteId, rowId, {
      backSource: "campaign",
      dateFrom,
      dateTo,
    });
    navigate(path, { state });
  };

  return (
    <>
      <h2 style={{ fontSize: "1rem", fontWeight: 600, margin: 0, paddingTop: "14px" }}>
        Campaign Performance
      </h2>
      <div className="dashboard_app_table dashboard_app_table--list-records">
        <div className="dashboard_table">
          <div ref={tableScrollRef} className="table_scroll_wrapper">
            <div
              ref={tableInnerRef}
              className={`table_inner table_y_scroll ${isAtScrollEnd ? "at-scroll-end" : ""}`}
              style={{
                maxHeight: tableMaxHeight ? `${tableMaxHeight}px` : "calc(100vh - 320px)",
                overflowY: "auto",
                overflowX: "auto",
              }}
            >
              <table className="table_outer">
                <thead>
                  <tr>
                    <th>Campaign name</th>
                    <th>Type</th>
                    <th>Media source</th>
                    <th>Clicks</th>
                    <th>Installs</th>
                    <th>Signups</th>
                    {metricColumns.map((m, i) => (
                      <th key={`metric-col-${m.id ?? i}`}>{composeEventMetricLabel(m.label || EVENT_METRIC_OPTION.label)}</th>
                    ))}
                    <th>Conversion %</th>
                    <th>Signup %</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={8 + metricColumns.length} className="text-center py-4">
                        Loading…
                      </td>
                    </tr>
                  ) : total === 0 ? (
                    <tr>
                      <td colSpan={8 + metricColumns.length} className="text-center py-4 text-muted">
                        No performance data for the selected period.
                      </td>
                    </tr>
                  ) : (
                    pageRows.map((row) => (
                      <tr
                        key={row.id}
                        className={appRouteId ? "campaign-perf-row--clickable" : undefined}
                        onClick={() => openCampaignDetail(row.id)}
                      >
                        <td>
                          <div style={{ lineHeight: 1.3 }}>
                            <strong>{row.name}</strong>
                            <div style={{ fontSize: "0.72em", color: "#999" }}>
                              {row.campaign_code}
                              {row.createdAt && (
                                <>
                                  {" "}
                                  &middot;{" "}
                                  {new Date(row.createdAt).toLocaleDateString("en-IN", {
                                    day: "2-digit",
                                    month: "short",
                                    year: "numeric",
                                  })}
                                </>
                              )}
                            </div>
                          </div>
                        </td>
                        <td>{getCampaignTypeLabel(row.campaign_type) || "—"}</td>
                        <td>{getMediaSourceLabel(row.channel) || "—"}</td>
                        <td>{bigAmountFormat(row.clicks)}</td>
                        <td>{bigAmountFormat(row.campaign_installs)}</td>
                        <td>{bigAmountFormat(row.campaign_signups)}</td>
                        {metricColumns.map((m, i) => (
                          <td key={`metric-cell-${m.id ?? i}`}>
                            {bigAmountFormat(
                              Array.isArray(row.metrics) ? row.metrics[i]?.amount : row.event_metric,
                              getAuthUserCurrency()
                            )}
                          </td>
                        ))}
                        <td>{row.conversion_rate}%</td>
                        <td>{row.signup_rate}%</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {!loading && (
              <ListPaginationBar
                page={safePage}
                setPage={setPage}
                total={total}
                pageSize={pageSize}
                totalPages={totalPages}
                ariaLabel="Performance pagination"
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
}
